import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Film, ArrowLeft, Heart, Share2, MapPin, Calendar, Globe, 
  Phone, Mail, DollarSign, Play, Image, FileText, Clock,
  Plus, Check, X, ChevronLeft, ChevronRight, ExternalLink, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TalentDetail = () => {
  const { talentId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shortlisted, setShortlisted] = useState(false);
  const [showShortlistModal, setShowShortlistModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [shortlistNotes, setShortlistNotes] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'casting_manager';

  useEffect(() => {
    fetchTalent();
    fetchProjects();
  }, [talentId]);

  const fetchTalent = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/talents/${talentId}`, { 
        headers, 
        withCredentials: true 
      });
      setTalent(response.data);
    } catch (error) {
      console.error('Error fetching talent:', error);
      navigate('/talents');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/projects`, { 
        headers, 
        withCredentials: true 
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleShortlist = async () => {
    if (!selectedProject) return;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/shortlists`, {
        talent_id: talentId,
        project_id: selectedProject,
        notes: shortlistNotes
      }, { headers, withCredentials: true });
      
      setShortlisted(true);
      setShowShortlistModal(false);
    } catch (error) {
      console.error('Error shortlisting:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/talents/${talentId}/status?status=${newStatus}`, {}, { 
        headers, 
        withCredentials: true 
      });
      setTalent(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleRequestContact = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/contact-requests?talent_id=${talentId}`, {}, { 
        headers, 
        withCredentials: true 
      });
      alert('Contact request submitted! An admin will review your request.');
    } catch (error) {
      if (error.response?.data?.detail === 'Request already pending') {
        alert('You already have a pending request for this contact.');
      } else {
        console.error('Error requesting contact:', error);
        alert('Failed to submit request. Please try again.');
      }
    }
  };

  // Helper function to convert Google Drive links to direct image URLs
  const getImageUrl = (url) => {
    if (!url) return null;
    
    if (url.includes('drive.google.com')) {
      let fileId = null;
      
      if (url.includes('/file/d/')) {
        fileId = url.split('/file/d/')[1]?.split('/')[0];
      } else if (url.includes('id=')) {
        fileId = url.split('id=')[1]?.split('&')[0];
      } else if (url.includes('/d/')) {
        fileId = url.split('/d/')[1]?.split('/')[0];
      }
      
      if (fileId) {
        return `https://drive.google.com/uc?export=view&id=${fileId}`;
      }
    }
    
    return url;
  };

  const defaultImages = [
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80',
    'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=800&q=80'
  ];

  // Process all talent photos through getImageUrl
  const processedImages = talent?.photos?.length > 0 
    ? talent.photos.map(p => getImageUrl(p)).filter(Boolean)
    : [];
  
  // Ensure we have at least default images if all photos fail
  const displayImages = processedImages.length > 0 ? processedImages : defaultImages;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  if (!talent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Talent Not Found</h2>
          <Button onClick={() => navigate('/talents')} className="btn-gold">Back to Directory</Button>
        </div>
      </div>
    );
  }

  const availabilityColors = {
    available: 'status-available',
    hold: 'status-hold',
    busy: 'status-busy'
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 nav-glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/talents')} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Directory</span>
            </button>

            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Film className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl tracking-wide">CASTDECK</span>
            </Link>

            <div className="flex items-center gap-3">
              <Button variant="outline" className="border-white/10" data-testid="share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
              <Button 
                onClick={() => setShowShortlistModal(true)}
                className={shortlisted ? 'bg-red-500 hover:bg-red-600' : 'btn-gold'}
                data-testid="shortlist-btn"
              >
                <Heart className={`w-4 h-4 mr-2 ${shortlisted ? 'fill-current' : ''}`} />
                {shortlisted ? 'Shortlisted' : 'Add to Shortlist'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Images */}
          <div className="lg:col-span-2 space-y-6">
            {/* Main Image */}
            <div 
              className="relative aspect-[4/3] bg-card overflow-hidden cursor-pointer"
              onClick={() => setShowImageModal(true)}
              data-testid="main-image"
            >
              <img 
                src={displayImages[currentImageIndex]} 
                alt={talent.full_name}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === 0 ? displayImages.length - 1 : prev - 1);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === displayImages.length - 1 ? 0 : prev + 1);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
              
              {/* Image Counter */}
              <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 text-sm">
                {currentImageIndex + 1} / {displayImages.length}
              </div>
            </div>

            {/* Thumbnail Strip */}
            {displayImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto hide-scrollbar">
                {displayImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 overflow-hidden ${
                      currentImageIndex === index ? 'ring-2 ring-primary' : 'opacity-50 hover:opacity-100'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Media Links */}
            <div className="grid grid-cols-3 gap-4">
              {talent.intro_video && (
                <a 
                  href={talent.intro_video} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-4 bg-card border border-border hover:border-primary/30 transition-colors"
                  data-testid="intro-video-link"
                >
                  <Play className="w-5 h-5 text-primary" />
                  <span>Intro Video</span>
                </a>
              )}
              {talent.showreel && (
                <a 
                  href={talent.showreel} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-4 bg-card border border-border hover:border-primary/30 transition-colors"
                  data-testid="showreel-link"
                >
                  <Image className="w-5 h-5 text-primary" />
                  <span>Showreel</span>
                </a>
              )}
              {talent.portfolio_pdf && (
                <a 
                  href={talent.portfolio_pdf} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-4 bg-card border border-border hover:border-primary/30 transition-colors"
                  data-testid="portfolio-link"
                >
                  <FileText className="w-5 h-5 text-primary" />
                  <span>Portfolio</span>
                </a>
              )}
            </div>
          </div>

          {/* Right Column - Info */}
          <div className="space-y-6">
            {/* Status & Name */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 text-xs font-bold uppercase ${availabilityColors[talent.availability] || 'bg-muted'}`}>
                  {talent.availability}
                </span>
                {talent.status === 'pending' && (
                  <span className="px-3 py-1 text-xs font-bold uppercase bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                    Pending Approval
                  </span>
                )}
              </div>
              <h1 className="text-4xl font-bold mb-2" data-testid="talent-name">{talent.full_name}</h1>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span>{talent.age} years old</span>
                <span className="capitalize">{talent.gender}</span>
                {talent.height && <span>{talent.height}</span>}
              </div>
            </div>

            {/* Location */}
            <div className="flex items-center gap-3 text-lg">
              <MapPin className="w-5 h-5 text-primary" />
              <span>{talent.location}</span>
            </div>

            {/* Look Tags */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Look & Style</h3>
              <div className="flex flex-wrap gap-2">
                {talent.look_tags?.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            </div>

            {/* Languages */}
            {talent.languages?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {talent.languages.map(lang => (
                    <span key={lang} className="px-3 py-1 bg-muted text-sm">{lang}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Experience & Budget */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-card border border-border">
                <div className="text-sm text-muted-foreground mb-1">Experience</div>
                <div className="font-semibold capitalize">{talent.experience_level}</div>
              </div>
              <div className="p-4 bg-card border border-border">
                <div className="text-sm text-muted-foreground mb-1">Budget Range</div>
                <div className="font-semibold">
                  {talent.budget_min && talent.budget_max 
                    ? `₹${talent.budget_min.toLocaleString()} - ₹${talent.budget_max.toLocaleString()}`
                    : 'Contact for rates'
                  }
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="p-4 bg-card border border-border space-y-3">
              {talent.union_status && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Union Status</span>
                  <span>{talent.union_status}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Travel Willing</span>
                <span>{talent.travel_willing ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {/* Admin Actions */}
            {isAdmin && talent.status === 'pending' && (
              <div className="p-4 bg-card border border-border">
                <h3 className="font-medium mb-4">Admin Actions</h3>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => handleStatusChange('approved')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="approve-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> Approve
                  </Button>
                  <Button 
                    onClick={() => handleStatusChange('rejected')}
                    variant="destructive"
                    className="flex-1"
                    data-testid="reject-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              </div>
            )}

            {/* Contact Info Section */}
            {(talent.phone || talent.email) && (
              <div className={`p-4 bg-card border ${talent.contact_access === 'full' ? 'border-primary/20' : 'border-border'}`}>
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  {isAdmin ? (
                    <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary">Admin Only</span>
                  ) : talent.contact_access === 'full' ? (
                    <span className="px-2 py-0.5 text-xs bg-green-500/10 text-green-400">Verified Access</span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs bg-yellow-500/10 text-yellow-400">Contact Protected</span>
                  )}
                  Contact Information
                </h3>
                
                {talent.phone_masked ? (
                  <div className="flex items-center gap-3 mb-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    <span className="italic">{talent.phone}</span>
                  </div>
                ) : talent.phone && (
                  <div className="flex items-center gap-3 mb-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{talent.phone}</span>
                  </div>
                )}
                
                {talent.email_masked ? (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <span className="italic">{talent.email}</span>
                  </div>
                ) : talent.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{talent.email}</span>
                  </div>
                )}
                
                {talent.contact_access === 'restricted' && (
                  <Button 
                    onClick={handleRequestContact}
                    className="w-full mt-4 bg-primary/10 hover:bg-primary/20 text-primary"
                    data-testid="request-contact-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" /> Request Contact Access
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Shortlist Modal */}
      <Dialog open={showShortlistModal} onOpenChange={setShowShortlistModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Add to Shortlist</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Add this talent to one of your project shortlists.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Select Project</label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="bg-input/20 border-white/10" data-testid="project-select">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Notes (Optional)</label>
              <Textarea
                value={shortlistNotes}
                onChange={(e) => setShortlistNotes(e.target.value)}
                placeholder="Add notes about this talent..."
                className="bg-input/20 border-white/10"
                data-testid="shortlist-notes"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowShortlistModal(false)} className="border-white/10">
                Cancel
              </Button>
              <Button onClick={handleShortlist} className="btn-gold" disabled={!selectedProject} data-testid="confirm-shortlist-btn">
                Add to Shortlist
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-4xl bg-black border-none p-0">
          <div className="relative">
            <img 
              src={displayImages[currentImageIndex]} 
              alt={talent.full_name}
              className="w-full max-h-[80vh] object-contain"
            />
            {displayImages.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === 0 ? displayImages.length - 1 : prev - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === displayImages.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TalentDetail;
